"use client";

import { useLang } from "@/contexts/language-context";
import { motion } from "framer-motion";
import { AlertCircle, ChefHat, Plus } from "lucide-react";

const MENU = [
  { cat: "App", items: ["Caesar Salad", "Burrata Toast", "Calamari"] },
  { cat: "Main", items: ["Ribeye 14oz", "Linguine Vongole", "Branzino"] },
  { cat: "Drink", items: ["House Cab", "Negroni", "Sparkling Water"] },
];

const ORDER = [
  { name: "Caesar Salad", mods: ["No anchovies"], course: "App" },
  { name: "Linguine Vongole", mods: ["Extra clams"], course: "Main", allergen: "Shellfish" },
  { name: "Ribeye 14oz", mods: ["Med-rare", "Side: spinach"], course: "Main" },
  { name: "House Cab", mods: ["Glass"], course: "Drink" },
];

export default function RestaurantsTableside() {
  const { t } = useLang();

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-[#FAFAFA] to-white">
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-[1fr_1.1fr] gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-redy-orange mb-4">
            {t("Tableside ordering", "Orden tableside")}
          </div>
          <h2 className="text-[40px] md:text-[60px] font-black leading-[1.02] tracking-tight text-redy-charcoal mb-5">
            {t("Tableside.", "Tableside.")}
            <br />
            <span className="redy-text-gradient">{t("Not register-side.", "No register-side.")}</span>
          </h2>
          <p className="text-lg text-redy-secondary leading-relaxed mb-6 max-w-[500px]">
            {t(
              "Servers take the order at the table, on the same iPad they use to fire the kitchen. No more walking across the room to a fixed station. No more handwritten dupes the line cook has to decode.",
              "El mesero toma la orden en la mesa, en el mismo iPad con el que dispara la cocina. Sin caminatas al fixed station. Sin dupes a mano que el cocinero tiene que descifrar."
            )}
          </p>

          <div className="space-y-3 max-w-[480px]">
            {[
              {
                en: "Menu, modifiers, notes — all one tap deep",
                es: "Menú, modificadores, notas — un tap de profundidad",
              },
              {
                en: "Recommendations & upsells surface in context",
                es: "Recomendaciones y upsells aparecen en contexto",
              },
              {
                en: "Fires to kitchen the moment they say 'yes'",
                es: "Dispara a cocina en el momento que dicen 'sí'",
              },
            ].map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-3"
              >
                <span className="flex-shrink-0 w-5 h-5 bg-redy-orange/15 text-redy-orange rounded-full flex items-center justify-center text-xs font-black mt-0.5">
                  ✓
                </span>
                <span className="text-sm text-redy-charcoal/80 font-medium leading-relaxed">
                  {t(row.en, row.es)}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tableside iPad mockup — held in hand */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative"
        >
          <div className="relative bg-redy-charcoal rounded-[28px] p-3 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] rotate-[-2deg]">
            <div className="bg-[#FDFCFA] rounded-[20px] overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-black/[0.05]">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50">
                    {t("Table 12 · 4 guests", "Mesa 12 · 4 invitados")}
                  </div>
                  <div className="text-sm font-black text-redy-charcoal">Maya · 7:58 PM</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50">
                    {t("Order #847", "Orden #847")}
                  </div>
                  <div className="text-sm font-bold text-redy-orange">$92.00</div>
                </div>
              </div>

              <div className="grid grid-cols-[110px_1fr]">
                {/* Category nav */}
                <div className="bg-[#F5F3EE] border-r border-black/[0.05] py-3 px-2">
                  {MENU.map((cat, i) => (
                    <div
                      key={cat.cat}
                      className={`px-3 py-2 rounded-lg text-xs font-bold mb-1 ${
                        i === 1 ? "bg-redy-charcoal text-white" : "text-redy-charcoal/70"
                      }`}
                    >
                      {cat.cat}
                    </div>
                  ))}
                </div>

                {/* Order being built */}
                <div className="p-4 min-h-[300px]">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-redy-charcoal/50 mb-2">
                    {t("Current order", "Orden actual")}
                  </div>
                  <div className="space-y-2">
                    {ORDER.map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.2 + i * 0.12 }}
                        className="bg-white border border-black/[0.05] rounded-lg p-2.5"
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-sm font-bold text-redy-charcoal">
                            {item.name}
                          </span>
                          <span
                            className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                              item.course === "Drink"
                                ? "bg-redy-blue/10 text-redy-blue"
                                : item.course === "App"
                                ? "bg-redy-orange/10 text-redy-orange"
                                : "bg-redy-charcoal/10 text-redy-charcoal"
                            }`}
                          >
                            {item.course}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1 text-[10px] text-redy-charcoal/60">
                          {item.mods.map((m, j) => (
                            <span
                              key={j}
                              className="bg-redy-charcoal/[0.05] px-1.5 py-0.5 rounded"
                            >
                              {m}
                            </span>
                          ))}
                          {item.allergen && (
                            <span className="inline-flex items-center gap-1 bg-redy-red/10 text-redy-red px-1.5 py-0.5 rounded font-bold">
                              <AlertCircle className="w-2.5 h-2.5" />
                              {item.allergen}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <motion.button
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 1.1 }}
                    className="w-full mt-3 inline-flex items-center justify-center gap-2 bg-redy-charcoal text-white py-2.5 rounded-lg text-xs font-bold"
                  >
                    <ChefHat className="w-3.5 h-3.5" />
                    {t("Fire to kitchen", "Disparar a cocina")}
                  </motion.button>

                  <button className="w-full mt-2 inline-flex items-center justify-center gap-2 border border-black/10 text-redy-charcoal py-2.5 rounded-lg text-xs font-bold">
                    <Plus className="w-3.5 h-3.5" />
                    {t("Add another item", "Agregar otro ítem")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* "Held in hand" suggestion */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/40">
            {t("Held by Maya, server", "En manos de Maya, mesera")}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
